<template>
  <resource-reader
    v-if="selectedId"
    :resource-id="selectedId"
    :is-admin="isAdmin"
    :can-acknowledge="canAcknowledge"
    @close="selectedId = null"
    @changed="load"
  />
  <section v-else class="sp-panel">
    <div class="sp-row sp-between">
      <div>
        <h2>Handbook & Forms</h2>
        <p class="sp-muted">
          Read the current policies and find the forms you need.
        </p>
      </div>
      <oxd-button
        v-if="isAdmin && !publishing"
        label="Publish a resource"
        display-type="secondary"
        @click="
          publishing = true;
          draft.category = category;
        "
      />
    </div>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!publishing"
      :success="success"
      @retry="load"
    />
    <form
      v-if="publishing && isAdmin"
      class="sp-notice"
      @submit.prevent="publish"
      @input="dirty = true"
    >
      <h3>Publish a new version</h3>
      <p>
        Published versions are preserved. Replacing a resource creates a new
        version with its own acknowledgment.
      </p>
      <fieldset :disabled="busy">
        <div class="sp-grid">
          <label class="sp-field"
            >Title<input v-model.trim="draft.title" maxlength="180" required
          /></label>
          <label class="sp-field"
            >Version<input
              v-model.trim="draft.version"
              maxlength="40"
              placeholder="For example: 2027.1"
              required
          /></label>
          <label class="sp-field"
            >Category<select
              v-model="draft.category"
              @change="changeCategory(draft.category)"
            >
              <option value="handbook">Handbook</option>
              <option value="forms">Forms</option>
              <option value="benefits">Benefits</option>
            </select></label
          >
          <label class="sp-field"
            >Resource type<select v-model="draft.kind">
              <option value="text">Policy text</option>
              <option value="link">Official link</option>
              <option value="file">Downloadable document</option>
            </select></label
          >
        </div>
        <label class="sp-field"
          >Summary<textarea
            v-model.trim="draft.summary"
            maxlength="1000"
            rows="2"
            required
          />
        </label>
        <label v-if="draft.kind === 'text'" class="sp-field"
          >Policy text<textarea v-model="draft.text" rows="12" required /><small
            >Use a blank line between paragraphs. Text is published as
            written.</small
          ></label
        >
        <label v-if="draft.kind === 'link'" class="sp-field"
          >Official resource URL<input
            v-model.trim="draft.url"
            type="url"
            pattern="https://.*"
            placeholder="https://"
            required
        /></label>
        <label v-if="draft.kind === 'file'" class="sp-field"
          >Document<input
            :key="fileKey"
            type="file"
            accept=".pdf,.docx"
            required
            @change="selectFile"
          /><small
            >PDF or DOCX, up to 5 MB. Publish a clean, blank form or approved
            policy document.</small
          ></label
        >
        <label class="sp-field"
          >Replace an existing version (optional)<select
            v-model="draft.replacesId"
          >
            <option value="">Publish as a separate resource</option>
            <option
              v-for="item in replaceOptions"
              :key="item.id"
              :value="item.id"
            >
              {{ item.title }} · {{ item.version }}
            </option>
          </select></label
        >
        <label class="sp-check"
          ><input v-model="draft.acknowledgmentRequired" type="checkbox" />
          Require staff to acknowledge this version</label
        >
        <div class="sp-actions">
          <oxd-button
            type="submit"
            label="Publish resource"
            display-type="secondary"
            :disabled="busy || readingFile"
          /><button type="button" class="sp-link" @click="closePublisher">
            Cancel
          </button>
        </div>
      </fieldset>
    </form>
    <div class="sp-filter" aria-label="Resource category">
      <button
        v-for="item in categories"
        :key="item.id"
        type="button"
        :aria-pressed="category === item.id"
        @click="changeCategory(item.id)"
      >
        {{ item.label }}
      </button>
    </div>
    <label class="sp-field"
      >Search resources<input
        v-model.trim="search"
        type="search"
        placeholder="Search titles, summaries, and policy text"
    /></label>
    <p v-if="indexing" class="sp-muted" role="status">
      Loading policy text for search…
    </p>
    <div v-if="!loading && !filteredItems.length" class="sp-empty">
      {{
        search
          ? 'No resources match your search.'
          : 'No resources have been published in this category yet.'
      }}
    </div>
    <div class="sp-grid">
      <article v-for="item in filteredItems" :key="item.id" class="sp-card">
        <p class="sp-eyebrow">
          {{ item.category }} · Version {{ item.version }}
        </p>
        <h3>{{ item.title }}</h3>
        <p class="sp-muted">{{ item.summary }}</p>
        <span v-if="item.acknowledgmentRequired" class="sp-badge">{{
          item.acknowledgedAt ? 'Acknowledged' : 'Acknowledgment required'
        }}</span>
        <p class="sp-muted">Published {{ displayDate(item.publishedAt) }}</p>
        <div class="sp-actions">
          <oxd-button
            label="Read resource"
            display-type="secondary"
            @click="openResource(item.id)"
          />
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import {ref, computed, onMounted} from 'vue';
import PortalFeedback from '../components/PortalFeedback.vue';
import ResourceReader from '../components/ResourceReader.vue';
import {portalApi, errorMessage, displayDate, useDraftGuard} from '../portal';

const newDraft = () => ({
  title: '',
  version: '',
  category: 'handbook',
  summary: '',
  kind: 'text',
  text: '',
  url: '',
  attachment: null,
  acknowledgmentRequired: false,
  replacesId: '',
});
export default {
  components: {PortalFeedback, ResourceReader},
  setup() {
    const api = portalApi('resources');
    const items = ref([]),
      search = ref(''),
      category = ref('handbook');
    const selectedId = ref(null),
      isAdmin = ref(false),
      canAcknowledge = ref(false);
    const loading = ref(false),
      indexing = ref(false),
      error = ref(''),
      success = ref('');
    const publishing = ref(false),
      busy = ref(false),
      readingFile = ref(false),
      fileKey = ref(0),
      dirty = ref(false);
    const draft = ref(newDraft());
    const {canLeave} = useDraftGuard(dirty);
    const categories = [
      {id: 'handbook', label: 'Handbook'},
      {id: 'forms', label: 'Forms'},
      {id: 'benefits', label: 'Benefit documents'},
    ];
    let generation = 0;
    const filteredItems = computed(() =>
      items.value.filter((item) =>
        `${item.title} ${item.summary} ${item.searchText || ''}`
          .toLowerCase()
          .includes(search.value.toLowerCase()),
      ),
    );
    const replaceOptions = computed(() =>
      items.value.filter((item) => item.category === draft.value.category),
    );
    async function load() {
      const request = ++generation;
      loading.value = true;
      error.value = '';
      items.value = [];
      try {
        const data = await api.list({category: category.value});
        if (request !== generation) return;
        items.value = data.items;
        isAdmin.value = data.isAdmin;
        canAcknowledge.value = data.canAcknowledge;
        loading.value = false;
        indexing.value = true;
        await Promise.all(
          data.items.map(async (item) => {
            try {
              const detail = await api.get(item.id);
              if (request === generation) {
                const row = items.value.find((entry) => entry.id === item.id);
                if (row)
                  row.searchText = (detail.content || [])
                    .map(
                      (section) =>
                        `${section.heading} ${section.paragraphs.join(' ')}`,
                    )
                    .join(' ');
              }
            } catch {
              /* Title and summary remain searchable when a detail cannot be loaded. */
            }
          }),
        );
      } catch (e) {
        if (request === generation) error.value = errorMessage(e);
      } finally {
        if (request === generation) {
          loading.value = false;
          indexing.value = false;
        }
      }
    }
    function changeCategory(value) {
      category.value = value;
      if (publishing.value) draft.value.category = value;
      draft.value.replacesId = '';
      load();
    }
    function openResource(id) {
      if (canLeave()) {
        dirty.value = false;
        publishing.value = false;
        draft.value = newDraft();
        selectedId.value = id;
      }
    }
    function closePublisher() {
      if (canLeave()) {
        publishing.value = false;
        dirty.value = false;
        draft.value = newDraft();
        fileKey.value++;
      }
    }
    async function selectFile(event) {
      const file = event.target.files?.[0];
      draft.value.attachment = null;
      error.value = '';
      if (!file) return;
      if (file.size > 5 * 1024 * 1024 || !/\.(pdf|docx)$/i.test(file.name)) {
        error.value = 'Choose a PDF or DOCX document no larger than 5 MB.';
        event.target.value = '';
        return;
      }
      readingFile.value = true;
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        draft.value.attachment = {name: file.name, base64};
        dirty.value = true;
      } catch {
        error.value = 'The document could not be read. Please select it again.';
      } finally {
        readingFile.value = false;
      }
    }
    async function publish() {
      if (!isAdmin.value || busy.value || readingFile.value) return;
      const value = draft.value;
      if (value.kind === 'file' && !value.attachment) {
        error.value = 'Choose a PDF or DOCX document to publish.';
        return;
      }
      const payload = {
        title: value.title,
        version: value.version,
        category: value.category,
        summary: value.summary,
        kind: value.kind,
        acknowledgmentRequired: value.acknowledgmentRequired,
      };
      if (value.kind === 'text')
        payload.content = [
          {
            heading: '',
            paragraphs: value.text
              .split(/\n\s*\n/)
              .map((text) => text.trim())
              .filter(Boolean),
          },
        ];
      if (value.kind === 'link') payload.url = value.url;
      if (value.kind === 'file') payload.attachment = value.attachment;
      if (value.replacesId) payload.replacesId = Number(value.replacesId);
      busy.value = true;
      error.value = '';
      success.value = '';
      try {
        await api.create(payload);
        category.value = value.category;
        dirty.value = false;
        publishing.value = false;
        draft.value = newDraft();
        fileKey.value++;
        await load();
        success.value = 'The new resource version is published.';
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    onMounted(load);
    return {
      items,
      search,
      category,
      categories,
      selectedId,
      isAdmin,
      canAcknowledge,
      loading,
      indexing,
      error,
      success,
      publishing,
      busy,
      readingFile,
      fileKey,
      dirty,
      draft,
      filteredItems,
      replaceOptions,
      displayDate,
      load,
      changeCategory,
      openResource,
      closePublisher,
      selectFile,
      publish,
    };
  },
};
</script>
